import { createSlice } from "@reduxjs/toolkit";

import { clearAccessToken } from "components/utilities/sedra3/auth";

// The access token itself is not kept here: it lives in auth.js, in memory
// only (#112), and the state holds what the page shows of the session.
const initialState = {
  user: null,
  roles: [],
  permissions: [],
  isAuthenticated: false,
};

// What a sign-in leaves in localStorage for the next page load: the user's
// profile and the account's roles and permissions, never a token. A reload
// restores them and renews the access token (store.js, index.jsx).
const SESSION_RECORDS = ["user", "roles", "permissions"];

// Where react-permission-role@2 reads the user for every AllowedAccess check:
// its setUser writes only this key, which it reads back at each check.
const PERMISSION_USER = "__permissionUser";

// Removed at sign-out with the session's records: what the permission library
// keeps of the user, the chosen language, the mark of the latest token handed
// between tabs (auth.js), and the tokens an earlier version of the portal kept
// here.
const OTHER_SESSION_KEYS = [
  PERMISSION_USER,
  "language",
  "sessionHandover",
  "accessToken",
  "refreshToken",
];

// The account's roles and permissions, as the server named them in the answer
// of the sign-in, the password change or a renewal, not as the access token
// does (VAPT Portal 2). They decide which pages and controls the page shows;
// the server authorises every request from its database, whatever the page
// holds. Kept in each place something reads them: the state, the records of the
// next page load, and the permission library's user.
const keepGrants = (state, { roles, permissions }) => {
  state.roles = roles;
  state.permissions = permissions;
  localStorage.setItem("roles", JSON.stringify(roles));
  localStorage.setItem("permissions", JSON.stringify(permissions));
  localStorage.setItem(
    PERMISSION_USER,
    JSON.stringify({ id: state.user?.id, roles, permissions })
  );
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // A sign-in or a password change: the user and the account's roles and
    // permissions, from the server's answer.
    setCredentials: (state, action) => {
      const { user, roles = [], permissions = [] } = action.payload;

      state.user = user;
      state.isAuthenticated = true;
      localStorage.setItem("user", JSON.stringify(user));
      keepGrants(state, { roles, permissions });
    },
    // A renewal named the account's roles and permissions as the server holds
    // them now (auth.js): a role approved or withdrawn since the sign-in shows
    // after the next page load, and records left stale or empty are replaced.
    grantsRenewed: (state, action) => {
      if (!state.isAuthenticated || !state.user) return;
      keepGrants(state, action.payload);
    },
    // A page load in a signed-in browser: the session as the sign-in recorded
    // it, until the renewal says whether it goes on (index.jsx).
    sessionRestored: (state, action) => {
      const { user, roles = [], permissions = [] } = action.payload;
      state.user = user;
      state.roles = roles;
      state.permissions = permissions;
      state.isAuthenticated = true;
    },
    logout: (state) => {
      state.user = null;
      state.roles = [];
      state.permissions = [];
      state.isAuthenticated = false;

      // The access token this tab holds in memory. Its cookies are the
      // server's, HttpOnly: the logout's answer removes them.
      clearAccessToken();
      // Every record of the session in localStorage. react-permission-role
      // keeps the user's id, roles and permissions in `__permissionUser` and
      // offers no call that clears them.
      [...SESSION_RECORDS, ...OTHER_SESSION_KEYS].forEach((key) =>
        localStorage.removeItem(key)
      );
    },
  },
});

// The session a signed-in browser recorded (setCredentials), or null.
export const recordedSession = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return null;
    return {
      user,
      roles: JSON.parse(localStorage.getItem("roles")) || [],
      permissions: JSON.parse(localStorage.getItem("permissions")) || [],
    };
  } catch {
    return null;
  }
};

export const { setCredentials, grantsRenewed, sessionRestored, logout } =
  authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectRoles = (state) => state.auth.roles;
export const selectPermissions = (state) => state.auth.permissions;

// Signed in and not signed out since. Whether the session is still good is the
// server's to say on the next request: a missing or expired access token is
// renewed there, and a session the server refuses signs the user out, which
// clears this.
export const selectHasSession = (state) => Boolean(state.auth.isAuthenticated);
